/**
 * Live view of an app lab check (#1712, spec §7): the check steps derived from
 * real agent events, and on a failure the phase, fail class, the last 10 events
 * and - when the agent uploaded one - the diagnosis bundle.
 */

import React from 'react';
import { Alert, Box, Button, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { LabEvent, LabJob } from '../../services/appsService';
import { deriveLabCheckSteps, lastFailure } from '../../utils/labCheckSteps';

interface Props {
  rootId: string;
  rootAppId: string;
  prereqIds: string[];
  job: LabJob | null;
  events: LabEvent[];
  onDownloadBundle: (appId: string) => void;
}

const STATE_TEXT = { pending: 'offen', running: 'läuft', done: 'erledigt', failed: 'fehlgeschlagen' } as const;

export default function LabCheckPanel({ rootId, rootAppId, prereqIds, job, events, onDownloadBundle }: Props) {
  const steps = deriveLabCheckSteps({ rootId, prereqIds, events, jobState: job?.state, failClass: job?.failClass });
  const failed = job?.outcome === 'failed' || steps.some((s) => s.state === 'failed');
  const failure = failed ? lastFailure(events, job?.failClass) : null;
  const bundles = job?.bundles ?? [];

  return (
    <Box>
      <Stepper orientation="vertical" activeStep={steps.findIndex((s) => s.state !== 'done')}>
        {steps.map((s) => (
          <Step key={s.label} completed={s.state === 'done'}>
            <StepLabel error={s.state === 'failed'}>
              {s.label} — {STATE_TEXT[s.state]}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {failure && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">Phase: {failure.phase}</Typography>
          <Typography variant="body2">Fehlerklasse: {failure.failClass}</Typography>
          {failure.appId && <Typography variant="body2">Rezept: {failure.appId}</Typography>}
          {job?.failDetail && <Typography variant="body2">{job.failDetail}</Typography>}
        </Alert>
      )}

      {failed && events.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Letzte Events</Typography>
          <Box component="pre" sx={{ fontSize: 12, whiteSpace: 'pre-wrap', m: 0 }}>
            {events
              .slice(-10)
              .map((e) => [e.phase, e.status, e.appId, e.failClass, e.exitCode, e.detail].filter((x) => x !== undefined && x !== null && x !== '').join(' '))
              .join('\n')}
          </Box>
        </Box>
      )}

      {failed &&
        bundles
          .filter((b) => b === rootAppId || prereqIds.some((p) => p.startsWith(`${b}@`)))
          .map((b) => (
            <Button key={b} variant="outlined" sx={{ mt: 2, mr: 1 }} onClick={() => onDownloadBundle(b)}>
              Diagnose-Bundle herunterladen ({b})
            </Button>
          ))}
    </Box>
  );
}
